import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  compararQuantidade,
  desescalar,
  multiplicarQuantidadePorCusto,
  somarDinheiro,
  subtrairDinheiro,
  subtrairQuantidade,
} from './aritmetica.ts';
import {
  custoUnitarioSchema,
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  quantidadeSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Devolução de consumo de obra (`.spec/015`, decisão 82, 26/09/2026).
 *
 * O material que saiu para a obra e **não foi aplicado** — os cinco sacos de
 * cimento lançados em 08/02 que em 10/04 ainda estavam no canteiro — volta a um
 * depósito e **sai do custo da obra**. É o inverso do consumo, e é por isso que é
 * um documento próprio e não um lançamento de estoque:
 *
 * - o lançamento de estoque de entrada devolvia o material ao depósito e deixava
 *   o custo na obra — o painel, o relatório da obra e o `CTR·05` continuavam
 *   dizendo que a Aurora gastou os cinco sacos;
 * - um consumo com quantidade negativa seria o segundo jeito de dar entrada,
 *   invisível na tela de estoque, e o `CHECK` de quantidade positiva do item
 *   existe justamente para recusá-lo.
 *
 * ## Amarrada ao consumo, linha a linha
 *
 * Cada linha da devolução aponta **o item de consumo** que devolve
 * (`lancamentoConsumoItemId`). Três coisas se seguem, e nenhuma é acidental:
 *
 * 1. **O teto é o que aquela linha baixou**, menos o que outras devoluções já
 *    trouxeram de volta. Devolver mais do que saiu criaria material do nada, e o
 *    crédito passaria a abater custo que a obra nunca teve.
 * 2. **O valor é o da baixa original**, e não o custo médio de hoje: o crédito
 *    tira da obra exatamente o que o consumo pôs nela. Com o custo de hoje, a
 *    obra ficaria com um resíduo — positivo ou negativo — que não corresponde a
 *    material nenhum. Ver `valorDaDevolucao`.
 * 3. **Obra, material e etapa vêm do consumo**, e não do payload. O crédito vai
 *    para a mesma etapa que recebeu o custo; aceitar outra deixaria a laje com o
 *    débito e a alvenaria com o crédito.
 *
 * O depósito de destino é livre (da mesma empresa): o material costuma voltar ao
 * almoxarifado central, e não ao canteiro de onde saiu.
 *
 * ## O que a devolução faz, na mesma transação
 *
 * Para cada linha, uma **entrada** em `movimentacoes_estoque` (a quinta origem,
 * `devolucao_consumo_item_id`) no depósito de destino, pelo valor da baixa, com
 * `atualiza_custo` desligado — devolver não é comprar, e o último custo é preço
 * de compra. O custo médio do destino se repondera, e deve: o material volta
 * valendo o que valia quando saiu.
 *
 * O crédito na obra **não é uma coluna** em lugar nenhum: é o próprio documento,
 * lido pelo painel, pelo relatório da obra e pelo `CTR·05` com o sinal trocado,
 * **na data da devolução**. O custo de fevereiro não muda em abril; abril ganha o
 * crédito. É o que faz o período fechado continuar devolvendo o mesmo número.
 */

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

/** Teto de linhas por devolução — o mesmo do consumo, e pelo mesmo motivo. */
export const MAXIMO_ITENS_DEVOLUCAO = 200;

const quantidadeDaDevolucaoSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade devolvida deve ser maior que zero.',
);

/**
 * Uma linha da devolução, como o cliente a manda.
 *
 * **Quatro ausências, e todas são a mesma decisão: o consumo já sabe.**
 *
 * - `insumoId` e `obraParteId` são os do item de consumo apontado. Recebê-los
 *   abriria o estado em que a devolução diz "cimento" sobre uma linha de areia.
 * - `valorUnitario` é o da baixa original, gravado no item de consumo.
 * - `valorTotal` sai de `valorDaDevolucao`, que fecha o valor da última
 *   devolução com o que sobrou do consumo, sem resíduo de arredondamento.
 */
export const itemDevolucaoEntradaSchema = z.object({
  /** O material de consumo que está voltando. */
  lancamentoConsumoItemId: uuidSchema,
  quantidade: quantidadeDaDevolucaoSchema,
  observacao: z
    .string()
    .trim()
    .max(500, 'A observação do material tem no máximo 500 caracteres.')
    .prefault(''),
});

export type ItemDevolucaoEntrada = z.infer<typeof itemDevolucaoEntradaSchema>;

export const MENSAGEM_ITEM_DE_DEVOLUCAO_REPETIDO =
  'Este material de consumo já está em outra linha. Some as quantidades numa linha só.';

/** Um item como a API o devolve, com o consumo de origem resolvido. */
export const itemDevolucaoSchema = z.object({
  id: uuidSchema,
  lancamentoConsumoItemId: uuidSchema,
  /** Número da requisição de consumo — "Consumo 412". */
  consumoNumero: z.number().int(),
  consumoData: dataSchema,
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  insumoUnidade: z.string().nullable(),
  insumoApenasInteiros: z.boolean(),
  obraParteId: uuidSchema.nullable(),
  obraParteNome: z.string().nullable(),
  quantidade: quantidadeSchema,
  /** O custo unitário da baixa original, com 6 casas. */
  valorUnitario: custoUnitarioSchema,
  /** O crédito desta linha na obra — e o valor que entrou no depósito. */
  valorTotal: dinheiroSchema,
  observacao: z.string(),
});

export type ItemDevolucao = z.infer<typeof itemDevolucaoSchema>;

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Payload de gravação.
 *
 * **Sem `numero`** (do contador da conta, `DEVOLUCAO_CONSUMO`) e **sem `tipo`**:
 * devolução é sempre entrada. A obra está no cabeçalho, e não é derivada dos
 * itens, porque é ela que recorta a lista do que se pode devolver na tela — e o
 * service confere que cada item é mesmo daquela obra.
 */
export const criarDevolucaoConsumoSchema = z
  .object({
    /** Empresa do grupo dona dos consumos e do depósito de destino. */
    empresaId: uuidSchema,
    /** Dia em que o material voltou. Não pode ser anterior ao consumo de nenhuma linha. */
    dataDevolucao: dataDoFatoSchema,
    /** Motivo do movimento. Precisa ser de sentido ENTRADA. */
    tipoMovimentacaoEstoqueId: uuidSchema,
    /** A obra que recebe o crédito — a mesma dos consumos devolvidos. */
    obraId: uuidSchema,
    /** Para onde o material volta. Um só, do cabeçalho, e da mesma empresa. */
    depositoId: uuidSchema,
    observacao: z
      .string()
      .trim()
      .max(1000, 'A observação tem no máximo 1000 caracteres.')
      .prefault(''),
    itens: z
      .array(itemDevolucaoEntradaSchema)
      .min(1, 'A devolução precisa de pelo menos um material.')
      .max(
        MAXIMO_ITENS_DEVOLUCAO,
        `A devolução tem no máximo ${MAXIMO_ITENS_DEVOLUCAO} materiais.`,
      ),
  })
  .superRefine((dados, ctx) => {
    /*
      A mesma linha de consumo duas vezes no documento.

      O índice único recusaria de qualquer forma; aqui o erro aponta a linha. A
      segunda leria o mesmo "ainda a devolver" que a primeira, e as duas juntas
      passariam do teto sem que nenhuma, sozinha, passasse.
    */
    const vistos = new Set<string>();
    dados.itens.forEach((item, indice) => {
      if (vistos.has(item.lancamentoConsumoItemId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itens', indice, 'lancamentoConsumoItemId'],
          message: MENSAGEM_ITEM_DE_DEVOLUCAO_REPETIDO,
        });
      }
      vistos.add(item.lancamentoConsumoItemId);
    });
  });

export type CriarDevolucaoConsumo = z.infer<typeof criarDevolucaoConsumoSchema>;

export const devolucaoConsumoSchema = z.object({
  id: uuidSchema,
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  dataDevolucao: dataSchema,
  tipoMovimentacaoEstoqueId: uuidSchema,
  tipoMovimentacaoEstoqueNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  obraId: uuidSchema,
  obraNome: z.string().nullable(),
  observacao: z.string(),
  /** Soma dos itens — o crédito que a obra recebeu por este documento. */
  valorTotal: dinheiroSchema,
  itens: z.array(itemDevolucaoSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type DevolucaoConsumo = z.infer<typeof devolucaoConsumoSchema>;

export const listaDevolucoesConsumoSchema = respostaListaSchema(devolucaoConsumoSchema);
export type ListaDevolucoesConsumo = z.infer<typeof listaDevolucoesConsumoSchema>;

export const listarDevolucoesConsumoQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  obraId: uuidSchema.optional(),
  depositoId: uuidSchema.optional(),
  insumoId: uuidSchema.optional(),
  /** As devoluções de uma requisição de consumo — o que a tela do consumo mostra. */
  lancamentoConsumoId: uuidSchema.optional(),
  de: dataSchema.optional(),
  ate: dataSchema.optional(),
});

export type ListarDevolucoesConsumoQuery = z.infer<typeof listarDevolucoesConsumoQuerySchema>;

export const COLUNAS_ORDENAVEIS_DEVOLUCAO_CONSUMO = [
  'numero',
  'dataDevolucao',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* O que se pode devolver                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Os filtros da lista de "o que ainda se pode devolver".
 *
 * `obraId` e `empresaId` obrigatórios: a devolução é da obra, e a lista sem obra
 * seria o consumo da conta inteira. `insumoId` e `lancamentoConsumoId`
 * estreitam — o segundo é o atalho "devolver desta requisição".
 */
export const devolvivelQuerySchema = z
  .object({
    empresaId: uuidSchema,
    obraId: uuidSchema,
    insumoId: uuidSchema.optional(),
    lancamentoConsumoId: uuidSchema.optional(),
  })
  .strict();

export type DevolvivelQuery = z.infer<typeof devolvivelQuerySchema>;

/**
 * Um item de consumo com saldo a devolver.
 *
 * As quatro grandezas vêm juntas porque `valorDaDevolucao` precisa das quatro, e
 * a tela mostra o valor enquanto se digita a quantidade — a conta é a mesma do
 * servidor, no contrato.
 */
export const itemDevolvivelSchema = z.object({
  lancamentoConsumoItemId: uuidSchema,
  lancamentoConsumoId: uuidSchema,
  consumoNumero: z.number().int(),
  consumoData: dataSchema,
  depositoOrigemId: uuidSchema,
  depositoOrigemNome: z.string().nullable(),
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  insumoUnidade: z.string().nullable(),
  insumoApenasInteiros: z.boolean(),
  obraParteId: uuidSchema.nullable(),
  obraParteNome: z.string().nullable(),
  quantidadeConsumida: quantidadeSchema,
  valorConsumido: dinheiroSchema,
  quantidadeDevolvida: quantidadeSchema,
  valorDevolvido: dinheiroSchema,
  /** `consumida − devolvida`: o teto da próxima devolução. */
  quantidadeDisponivel: quantidadeSchema,
  valorUnitario: custoUnitarioSchema,
});

export type ItemDevolvivel = z.infer<typeof itemDevolvivelSchema>;

export const listaDevolvivelSchema = z.object({ data: z.array(itemDevolvivelSchema) });
export type ListaDevolvivel = z.infer<typeof listaDevolvivelSchema>;

/**
 * Teto da lista de devolvíveis. Uma obra grande tem milhares de linhas de
 * consumo; a tela busca por material, e o teto evita trazer o histórico inteiro
 * para a memória do navegador.
 */
export const LIMITE_ITENS_DEVOLVIVEIS = 500;

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas                                                      */
/* -------------------------------------------------------------------------- */

/** O que se sabe de um item de consumo quando se vai devolver parte dele. */
export interface SaldoDoConsumo {
  quantidadeConsumida: string;
  valorConsumido: string;
  quantidadeDevolvida: string;
  valorDevolvido: string;
  valorUnitario: string;
}

/** Quanto ainda se pode devolver desta linha de consumo. */
export function quantidadeDevolvivel(saldo: SaldoDoConsumo): string {
  return subtrairQuantidade(saldo.quantidadeConsumida, saldo.quantidadeDevolvida);
}

/** A quantidade passa do que ainda se pode devolver? */
export function devolucaoPassaDoConsumo(saldo: SaldoDoConsumo, quantidade: string): boolean {
  return compararQuantidade(quantidade, quantidadeDevolvivel(saldo)) > 0;
}

/**
 * O valor de uma devolução — o crédito na obra e a entrada no depósito.
 *
 * **Parcial**: `quantidade × valor unitário da baixa`, a centavo.
 *
 * **A que zera o consumo**: o valor que **sobrou** do consumo, inteiro — é a
 * mesma regra de `valorDaSaida`, do outro lado. Três devoluções de um terço
 * arredondadas cada uma somariam um centavo a mais ou a menos do que o consumo
 * baixou, e a obra que devolveu tudo ficaria com R$ 0,01 de custo de material
 * que não existe.
 *
 * No contrato, e não só no service, porque a tela mostra o crédito enquanto se
 * digita — e duas contas discordariam no arredondamento.
 */
export function valorDaDevolucao(saldo: SaldoDoConsumo, quantidade: string): string {
  if (compararQuantidade(quantidade, quantidadeDevolvivel(saldo)) === 0) {
    return subtrairDinheiro(saldo.valorConsumido, saldo.valorDevolvido);
  }
  return desescalar(multiplicarQuantidadePorCusto(quantidade, saldo.valorUnitario), CASAS_DINHEIRO);
}

/** A soma dos itens, a centavo. */
export function totalDaDevolucao(itens: readonly { valorTotal: string }[]): string {
  return somarDinheiro(itens.map((item) => item.valorTotal));
}
